import React from "react";
import { NavLink } from "react-router-dom";
import { ArrowRight, ShieldCheck, LineChart, Layers, Sparkles, CheckCircle2, Landmark, FileBarChart2, Compass, Cog, Search } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";

const IMG = {
  hero: "/images/hero-wind.jpg",
  lithium: "/images/lithium-lab.jpg",
  research: "/images/research.jpg",
  wind: "/images/wind-farm.jpg",
  solar: "/images/solar.jpg",
  dashboard: "/images/dashboard.jpg",
  mining: "/images/mining.jpg",
  analytics: "/images/analytics.jpg",
};

export default function Home() {
  const { t } = useI18n();

  const whyKeys = ["strategic", "digital", "variety", "impact"];
  const whyIcons = { strategic: FileBarChart2, digital: Cog, variety: Layers, impact: LineChart };

  const projectKeys = ["lithium", "wind", "solar"];
  const projectImages = { lithium: IMG.lithium, wind: IMG.wind, solar: IMG.solar };

  const stepKeys = ["explore", "define", "track", "analyze"];
  const stepIcons = { explore: Search, define: Compass, track: LineChart, analyze: FileBarChart2 };

  const badgeKeys = ["amf", "esg", "audit", "eu"];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0">
          <img
            src={IMG.hero}
            alt={t("hero.image_alt")}
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/70" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 pb-24 pt-20 sm:px-6 lg:px-10 lg:pb-32 lg:pt-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> {t("hero.eyebrow")}
          </span>
          <h1
            data-testid="hero-headline-title"
            className="mt-6 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
          >
            {t("hero.title")}
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-200 sm:text-lg">
            {t("hero.intro_1")}
          </p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300">
            {t("hero.intro_2")}
          </p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300">
            {t("hero.intro_3")}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#newsletter"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              {t("hero.cta_primary")}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#sectors"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>
        </div>
      </section>

      {/* KPI STRIP */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("kpi.heading")}
            </h2>
            <img
              src={IMG.dashboard}
              alt=""
              aria-hidden="true"
              className="hidden h-12 w-24 rounded-md object-cover sm:block"
            />
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {["lithium", "wind", "solar", "esg"].map((k) => (
              <div
                key={k}
                data-testid={`kpi-card-${k}`}
                className="rounded-xl border border-slate-200 bg-white p-6"
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                  {t(`kpi.items.${k}.label`)}
                </p>
                <p className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-900">
                  {t(`kpi.items.${k}.value`)}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {t(`kpi.items.${k}.hint`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="sectors" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
              {t("why.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("why.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {whyKeys.map((k) => {
              const Icon = whyIcons[k];
              return (
                <div
                  key={k}
                  data-testid={`why-card-${k}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-6"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-emerald-600 text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">
                    {t(`why.items.${k}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {t(`why.items.${k}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
              {t("projects.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("projects.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projectKeys.map((k) => (
              <article
                key={k}
                data-testid={`project-card-${k}`}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white"
              >
                <div className="h-56 w-full overflow-hidden">
                  <img
                    src={projectImages[k]}
                    alt={t(`projects.cards.${k}.title`)}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                    {t(`projects.cards.${k}.region`)}
                  </p>
                  <h3 className="mt-2 font-heading text-xl font-semibold text-slate-900">
                    {t(`projects.cards.${k}.title`)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {t(`projects.cards.${k}.description`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CIRCUIT */}
      <section id="circuit" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                {t("circuit.eyebrow")}
              </p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {t("circuit.title")}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                {t("circuit.intro")}
              </p>
              <img
                src={IMG.analytics}
                alt=""
                aria-hidden="true"
                className="mt-8 hidden h-64 w-full rounded-xl object-cover lg:block"
              />
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-5">
                {stepKeys.map((k, i) => {
                  const Icon = stepIcons[k];
                  return (
                    <li
                      key={k}
                      data-testid={`circuit-step-${k}`}
                      className="flex gap-5 rounded-xl border border-slate-200 bg-slate-50 p-6"
                    >
                      <div className="flex flex-col items-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-slate-900 text-white">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="mt-2 text-xs font-semibold text-emerald-700">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-semibold text-slate-900">
                          {t(`circuit.steps.${k}.title`)}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">
                          {t(`circuit.steps.${k}.description`)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section
        id="transparency"
        className="bg-slate-950 text-white"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src={IMG.research}
                alt={t("transparency.title")}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
              {t("transparency.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t("transparency.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-200">
              {t("transparency.paragraph_1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-300">
              {t("transparency.paragraph_2")}
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {badgeKeys.map((k) => (
                <div
                  key={k}
                  data-testid={`transparency-badge-${k}`}
                  className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 p-4"
                >
                  {k === "amf" ? (
                    <Landmark className="h-5 w-5 text-emerald-400" />
                  ) : k === "audit" ? (
                    <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  )}
                  <span className="text-sm font-medium text-white">
                    {t(`transparency.badges.${k}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-10">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 sm:p-12">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                {t("newsletter.eyebrow")}
              </p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {t("newsletter.title")}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                {t("newsletter.description")}
              </p>
            </div>
            <div className="mt-10 rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
              <NewsletterForm variant="footer" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
